import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  Select,
} from "../components";
import { usePermission, useUser } from "../account/AccountStore";
import { Permissions, Permission } from "../account/permissions";
import { useAction, useAsync } from "../general/useAsync";
import { StudentModuleEnrollmentSyncModel } from "../api/APIClient";
import * as Management from "../management/ManagementStore";
import * as Configuration from "../configuration/ConfigurationStore";
import * as Activities from "../activities/ActivitiesStore";
import { findStudentEnrollment } from "./MarkImportStore";

const setupSteps: Array<{
  title: string;
  description: string;
  links: Array<{ path: string; label: string; permission: Permission }>;
}> = [
  {
    title: "1. Define the curriculum",
    description:
      "Reuse your existing catalogue or create the qualification, grade and subject you need. Any grade is supported.",
    links: [
      {
        path: "/structure/faculties",
        label: "Faculties",
        permission: Permissions.STRUCTURE,
      },
      {
        path: "/structure/schools",
        label: "Schools",
        permission: Permissions.STRUCTURE,
      },
      {
        path: "/structure/qualifications",
        label: "Qualifications",
        permission: Permissions.STRUCTURE,
      },
      {
        path: "/structure/qualification-levels",
        label: "Qualification levels",
        permission: Permissions.STRUCTURE,
      },
      {
        path: "/structure/modules",
        label: "Modules",
        permission: Permissions.STRUCTURE,
      },
    ],
  },
  {
    title: "2. Offer the subject for a cohort and term",
    description:
      "A qualification module joins the subject to a programme level and assessment cycle. Check the academic year carefully.",
    links: [
      {
        path: "/management/academic-years",
        label: "Academic years",
        permission: Permissions.MANAGEMENT,
      },
      {
        path: "/management/cycles",
        label: "Assessment cycles",
        permission: Permissions.MANAGEMENT,
      },
      {
        path: "/management/academic-programs",
        label: "Academic programs",
        permission: Permissions.MANAGEMENT,
      },
      {
        path: "/management/program-levels",
        label: "Program levels",
        permission: Permissions.MANAGEMENT,
      },
      {
        path: "/management/qualification-modules",
        label: "Qualification modules",
        permission: Permissions.MANAGEMENT,
      },
    ],
  },
  {
    title: "3. Configure the mark target and learners",
    description:
      "Create a group and assessment with its maximum and choose a mark type. Missing learner enrollments can be created when marks are imported; existing inactive or ambiguous records need review.",
    links: [
      {
        path: "/configuration",
        label: "Mark, assessment and group types",
        permission: Permissions.CONFIGURATION_READ,
      },
      {
        path: "/activities",
        label: "Assessment structure",
        permission: Permissions.ACTIVITIES_READ,
      },
      {
        path: "/enrollment/modules",
        label: "Module enrollment",
        permission: Permissions.ENROLLMENT,
      },
      {
        path: "/management/teaching-groups",
        label: "Teaching groups (optional)",
        permission: Permissions.MANAGEMENT,
      },
      {
        path: "/enrollment/teaching-groups",
        label: "Teaching group members (optional)",
        permission: Permissions.ENROLLMENT,
      },
    ],
  },
];

export const MarkImportSetup: React.FC = () => {
  const user = useUser();
  const canRead = usePermission(Permissions.CONFIGURATION_READ);
  const canManage = usePermission(Permissions.MANAGEMENT);
  const canReadActivities = usePermission(Permissions.ACTIVITIES_READ);
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleID = searchParams.get("module") ?? "";
  const modules = useAsync(Management.listQualificationModules, [], {
    enabled: canRead && canManage,
  });
  const markTypes = useAsync(Configuration.listMarkTypes, [], {
    enabled: canRead,
  });
  const selectedModule = modules.data?.find((module) => module.id === moduleID);

  if (!canRead) {
    return (
      <Banner>
        Configuration read permission is required to connect an application.
      </Banner>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-text-strong">
          Choose where the application will send marks
        </h3>
        <p className="mt-1 text-sm text-text-weak">
          Select an existing assessment and give its target IDs to the sending
          application. The application saves the mapping and controls when to
          send results.
        </p>
      </div>
      <Card>
        <CardBody>
          <h3 className="mb-4 font-semibold">
            1. Choose the subject and academic year
          </h3>
          {!canManage && (
            <Banner tone="warning">
              Management permission is required to select subject offerings.
            </Banner>
          )}
          {modules.error && <Banner>{modules.error}</Banner>}
          {markTypes.error && <Banner>{markTypes.error}</Banner>}
          <Field
            label="Subject offering"
            hint="Choose the subject, year and term that will receive the marks."
          >
            <Select
              aria-label="Subject offering"
              value={moduleID}
              disabled={!canManage || modules.isLoading}
              placeholder="Select a subject offering"
              onChange={(event) =>
                setSearchParams(
                  event.target.value ? { module: event.target.value } : {},
                )
              }
            >
              {(modules.data ?? []).map((module) => (
                <option key={module.id} value={module.id}>
                  {Management.qualificationModuleLabel(module)}
                </option>
              ))}
            </Select>
          </Field>
          {selectedModule && !modules.isLoading && !modules.error && (
            <>
              <p className="mb-5 text-sm text-text-weak">
                Academic year:{" "}
                {selectedModule.qualificationProgramLevel?.academicYear?.name ??
                  "Not set"}
                . Programme:{" "}
                {selectedModule.qualificationProgramLevel?.academicProgram
                  ?.name ?? "Not set"}
                .
              </p>
              {canReadActivities ? (
                <TargetDetails
                  key={moduleID}
                  module={selectedModule}
                  markTypes={markTypes.error ? [] : (markTypes.data ?? [])}
                />
              ) : (
                <Banner tone="warning">
                  Assessment structure read permission is required to inspect
                  targets.
                </Banner>
              )}
            </>
          )}
        </CardBody>
      </Card>
      <details className="rounded-card border border-border bg-surface p-4 text-sm">
        <summary className="cursor-pointer font-medium">
          Need to create a subject or assessment?
        </summary>
        <p className="my-3 text-text-weak">
          Use the existing assessment management screens, then return here to
          select your target.
        </p>
        <ol className="space-y-4">
          {setupSteps.map((step) => (
            <li key={step.title}>
              <h4 className="font-semibold">{step.title}</h4>
              <p className="mt-1 text-text-weak">{step.description}</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                {step.links
                  .filter((link) => user.hasPermission(link.permission))
                  .map((link) => (
                    <Link
                      key={link.path}
                      className="text-primary-text underline"
                      to={
                        link.path === "/activities" && moduleID
                          ? `${link.path}?module=${encodeURIComponent(moduleID)}`
                          : link.path
                      }
                    >
                      {link.label}
                    </Link>
                  ))}
              </div>
            </li>
          ))}
        </ol>
      </details>
      <details className="rounded-card border border-border bg-surface p-4 text-sm">
        <summary className="cursor-pointer font-medium">
          Application access in Identity
        </summary>
        <div className="mt-3 space-y-3">
          <h4 className="font-semibold">
            Authorize the application in Identity
          </h4>
          <p>
            Register or reuse the application's OAuth client in Identity and
            grant <code>assessment:marks.write</code> for this organisation.
          </p>
          <p>
            Assessment uses the existing mark-write permission; there is no
            separate connection or grant to configure here.
          </p>
          <p>
            Supply an authenticated OAuth bearer token with the verified{" "}
            <code>client_id</code> and an organisation context or membership
            that authorizes this organisation.
          </p>
          <p>
            Assessment derives the source namespace from the authenticated
            Identity client ID. Keep the same OAuth client when retrying or
            correcting its marks.
          </p>
        </div>
      </details>
    </div>
  );
};

const TargetDetails: React.FC<{
  module: Management.QualificationModuleAPIModel;
  markTypes: Configuration.MarkTypeModel[];
}> = ({ module, markTypes }) => {
  const canEnroll = usePermission(Permissions.ENROLLMENT);
  const structure = useAsync(async () => {
    try {
      return await Activities.getModuleStructure(module.id ?? "");
    } catch {
      throw new Error(
        "Assessment structure is unavailable. Check your access and try again.",
      );
    }
  }, [module.id]);
  const [assessmentID, setAssessmentID] = React.useState("");
  const [markTypeID, setMarkTypeID] = React.useState("");
  const [studentID, setStudentID] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    setCopied(false);
  }, [assessmentID, markTypeID]);
  const copyTarget = useAction(async () => {
    if (!navigator.clipboard) {
      throw new Error(
        "Copy is unavailable in this browser. Select and copy the target IDs below.",
      );
    }
    await navigator.clipboard.writeText(
      JSON.stringify({ assessmentID, markTypeID }, null, 2),
    );
    setCopied(true);
  });
  const [enrollments, setEnrollments] = React.useState<
    StudentModuleEnrollmentSyncModel[] | null
  >(null);
  const activeEnrollments =
    enrollments?.filter(
      (enrollment) => enrollment.enrollmentStatus === "Enrolled",
    ) ?? [];
  const assessment = structure.data?.assessments?.find(
    (item) => item.id === assessmentID,
  );
  const lookup = useAction(async () => {
    setEnrollments(null);
    const matches = await findStudentEnrollment(
      studentID.trim(),
      module.moduleID ?? "",
      module.id ?? "",
    );
    setEnrollments(matches);
  });

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">2. Choose the assessment and mark type</h3>
      {structure.error && <Banner>{structure.error}</Banner>}
      <Field label="Assessment activity">
        <Select
          aria-label="Assessment activity"
          value={assessmentID}
          disabled={structure.isLoading || !!structure.error}
          placeholder="Select an assessment"
          onChange={(event) => setAssessmentID(event.target.value)}
        >
          {(structure.error ? [] : (structure.data?.assessments ?? [])).map(
            (item) => (
              <option key={item.id} value={item.id}>
                {item.name} (out of {item.total})
              </option>
            ),
          )}
        </Select>
      </Field>
      {!structure.isLoading &&
        !structure.error &&
        !structure.data?.assessments?.length && (
          <Banner tone="warning">
            No assessment activities yet. Use Assessment structure to add a
            group and an assessment.
          </Banner>
        )}
      <Field label="Mark type">
        <Select
          aria-label="Mark type"
          value={markTypeID}
          placeholder="Select a mark type"
          onChange={(event) => setMarkTypeID(event.target.value)}
        >
          {markTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </Select>
      </Field>
      {assessment && !structure.isLoading && !structure.error && (
        <div className="space-y-3">
          <h3 className="font-semibold">3. Connect the sending application</h3>
          <Identifier label="Assessment ID" value={assessment.id} />
          {markTypes.some((type) => type.id === markTypeID) && (
            <Identifier label="Mark type ID" value={markTypeID} />
          )}
          <p className="text-sm">
            Maximum mark: <strong>{assessment.total}</strong> points.
          </p>
        </div>
      )}
      {assessment &&
        markTypes.some((type) => type.id === markTypeID) &&
        !structure.error &&
        !structure.isLoading && (
          <div className="space-y-3 rounded-card border border-border bg-surface-muted p-4 text-sm">
            <p>Give these two IDs to the application that will send marks.</p>
            <p>
              In Service Manager, open{" "}
              <strong>External Services → Assessment</strong>, enter the IDs and
              enable <strong>Allow updates</strong>.
            </p>
            <p>
              Missing learner enrolments are created when a mark is received.
              You do not need to enrol each learner here first.
            </p>
            <Button onClick={copyTarget.run} loading={copyTarget.busy}>
              Copy target IDs
            </Button>
            {copied && <p role="status">Target IDs copied.</p>}
            {copyTarget.error && <Banner>{copyTarget.error}</Banner>}
          </div>
        )}
      {canEnroll && (
        <details className="rounded-card border border-border p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            Check a learner's enrolment (optional)
          </summary>
          <section
            aria-label="Learner enrollment lookup"
            className="space-y-3 border-t border-border pt-4"
          >
            <Field
              label="Learner Identity user ID"
              hint="Use the exact Identity user ID to inspect existing enrollment. Missing enrollment can be created on import; a prior enrollment sync is not required."
            >
              <Input
                aria-label="Learner Identity user ID"
                value={studentID}
                disabled={lookup.busy}
                onChange={(event) => {
                  setStudentID(event.target.value);
                  setEnrollments(null);
                  lookup.clearError();
                }}
              />
            </Field>
            <Button
              disabled={!studentID.trim() || !module.moduleID}
              loading={lookup.busy}
              onClick={lookup.run}
            >
              Find learner enrollment
            </Button>
            {lookup.error && <Banner>{lookup.error}</Banner>}
            {enrollments?.length === 0 && (
              <Banner tone="info">
                No subject enrollment found for this learner in the selected
                offering. Importing a mark without studentModuleEnrollmentID
                will create missing subject and grade/programme enrollments.
                Existing inactive or ambiguous grade/programme records still
                need review.
              </Banner>
            )}
            {activeEnrollments.length > 1 && (
              <Banner tone="warning">
                Multiple active enrollments found. Resolve ambiguous records
                before using automatic enrollment resolution.
              </Banner>
            )}
            {enrollments !== null &&
              enrollments.length > 0 &&
              activeEnrollments.length === 0 && (
                <Banner tone="warning">
                  No active enrollment found. Existing inactive or completed
                  records need the enrollment workflow; imports do not
                  reactivate them.
                </Banner>
              )}
            {enrollments?.map((enrollment) => (
              <div key={enrollment.id} className="space-y-2">
                <Identifier
                  label="Student module enrollment ID"
                  value={enrollment.id}
                />
                <p className="text-sm">Status: {enrollment.enrollmentStatus}</p>
              </div>
            ))}
          </section>
        </details>
      )}
      {assessment &&
        markTypes.some((type) => type.id === markTypeID) &&
        !structure.isLoading &&
        !structure.error && (
          <details className="rounded-card border border-border p-3 text-sm">
            <summary className="cursor-pointer font-medium">
              Publication request example
            </summary>
            <div className="my-3 space-y-2">
              <p>
                POST /api/assessment/mark-imports. Supply an authenticated OAuth
                bearer token with assessment:marks.write for this organisation
                and a verified client_id. Assessment derives the source
                namespace from that client ID; do not send a connection ID or
                source system key.
              </p>
              <p>
                Replace every angle-bracket placeholder. Use a new non-empty
                UUID for requestID, a stable sourceRecordID for this result, and
                explicit sourceVersion and mappingVersion values. Keep the
                request ID and complete payload unchanged for retries. A first
                publication uses revision 1 and previousRevision 0; a correction
                increments the accepted revision.
              </p>
              <p>
                studentID must be the exact Identity user ID.
                studentModuleEnrollmentID is optional: omit it to resolve an
                active enrollment in this exact subject offering, year and
                cycle, or create missing subject and grade/programme enrollments
                with the mark. Imports do not reactivate inactive or completed
                records or choose between ambiguous active records.
              </p>
              <p>
                If supplied, studentModuleEnrollmentID must identify this
                learner's exact active enrollment. Receipts return the resolved
                enrollment ID; do not add it to the original payload when
                retrying.
              </p>
              <p>
                result is numeric raw points from zero through
                expectedAssessmentTotal, with at most six decimal places. Zero
                is a valid earned mark. The expected total must equal the
                activity's current maximum. This example does not publish a
                mark.
              </p>
            </div>
            <div className="my-3 space-y-2">
              <p>
                Read a receipt: GET
                /api/assessment/mark-imports/requests/&lt;requestID&gt;.
              </p>
              <p>
                Read the latest source revision: GET
                /api/assessment/mark-imports/records?sourceRecordID=&lt;sourceRecordID&gt;.
              </p>
              <p>
                Retry pending calculations: POST
                /api/assessment/mark-imports/recalculate with sourceRecordID and
                expectedRevision. Use the same application's bearer token.
              </p>
            </div>
            <pre
              className="max-w-full overflow-x-auto rounded bg-surface-muted p-3 text-xs"
              tabIndex={0}
              aria-label="Mark publication JSON example"
            >
              {JSON.stringify(
                {
                  requestID: "<new UUID>",
                  sourceRecordID: "<stable source result ID>",
                  revision: 1,
                  previousRevision: 0,
                  sourceVersion: "<source revision>",
                  mappingVersion: "<mapping revision>",
                  assessmentID: assessment.id,
                  studentID: studentID.trim() || "<Identity user ID>",
                  markTypeID,
                  result: 0,
                  expectedAssessmentTotal: assessment.total,
                },
                null,
                4,
              )}
            </pre>
          </details>
        )}
      <div className="flex flex-wrap gap-4 text-sm">
        <Link
          className="text-primary-text underline"
          to={`/activities?module=${encodeURIComponent(module.id ?? "")}`}
        >
          Edit selected assessment structure
        </Link>
        {canEnroll && (
          <Link
            className="text-primary-text underline"
            to="/enrollment/modules"
          >
            Manage learner enrollment
          </Link>
        )}
      </div>
    </div>
  );
};

const Identifier: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <Field label={label} className="mb-0">
    <Input
      aria-label={label}
      className="font-mono"
      value={value ?? ""}
      readOnly
      onFocus={(event) => event.currentTarget.select()}
    />
  </Field>
);

export default MarkImportSetup;
